import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { Company } from '../types';

export const OPERATIONAL_COMPANY_CODES = [
  'SKI',
  'TCI',
  'SPPL/LDH',
  'SPPL/GGN',
  'SCPL/LDH',
  'SCPL/KOL',
];

interface CompanyContextType {
  companies: Company[];
  selectedCompanyId: number | 'all';
  selectedCompany: Company | null;
  setSelectedCompanyId: (id: number | 'all') => void;
  loading: boolean;
  refreshCompanies: () => Promise<void>;
  updateCompany: (id: number, updates: Partial<Company>) => Promise<boolean>;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export function CompanyProvider({ children }: { children: React.ReactNode }) {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyIdState] = useState<number | 'all'>(() => {
    const saved = localStorage.getItem('ski_selected_company_id');
    if (saved && saved !== 'all') {
      const parsed = parseInt(saved, 10);
      return isNaN(parsed) ? 'all' : parsed;
    }
    return 'all';
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCompanies();
  }, []);

  // When the Supabase session resolves/restores (e.g. page reload with an
  // existing session), re-fetch so rows protected by authenticated-only RLS
  // policies are included — the very first fetch can race session restore.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN') {
        fetchCompanies();
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  async function fetchCompanies() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;

      // Strict filter: only verified operational legal entities
      const operational = (data || []).filter((c) =>
        OPERATIONAL_COMPANY_CODES.includes(c.code)
      );

      // Merge with any local user overrides for offline/instant persistence
      const localOverridesStr = localStorage.getItem('ski_company_overrides');
      const overrides: Record<number, Partial<Company>> = localOverridesStr
        ? JSON.parse(localOverridesStr)
        : {};

      const merged = operational.map((c) => {
        if (c.id && overrides[c.id]) {
          return { ...c, ...overrides[c.id] };
        }
        return c;
      });

      setCompanies(merged);
    } catch (err) {
      console.warn('Company fetch error, using fallback operational list:', err);
    } finally {
      setLoading(false);
    }
  }

  function setSelectedCompanyId(id: number | 'all') {
    setSelectedCompanyIdState(id);
    localStorage.setItem('ski_selected_company_id', String(id));
  }

  async function updateCompany(id: number, updates: Partial<Company>): Promise<boolean> {
    try {
      // 1. Try Supabase update
      const { error } = await supabase
        .from('companies')
        .update(updates)
        .eq('id', id);

      if (error) {
        console.warn('Supabase RLS note on company update, caching locally:', error.message);
      }

      // 2. Persist in local overrides cache
      const localOverridesStr = localStorage.getItem('ski_company_overrides');
      const overrides: Record<number, Partial<Company>> = localOverridesStr
        ? JSON.parse(localOverridesStr)
        : {};
      overrides[id] = { ...(overrides[id] || {}), ...updates };
      localStorage.setItem('ski_company_overrides', JSON.stringify(overrides));

      // 3. Update memory state
      setCompanies((prev) =>
        prev.map((c) => (c.id === id ? { ...c, ...updates } : c))
      );

      return true;
    } catch (err) {
      console.error('Update company error:', err);
      return false;
    }
  }

  const selectedCompany =
    selectedCompanyId === 'all'
      ? null
      : companies.find((c) => c.id === selectedCompanyId) || null;

  return (
    <CompanyContext.Provider
      value={{
        companies,
        selectedCompanyId,
        selectedCompany,
        setSelectedCompanyId,
        loading,
        refreshCompanies: fetchCompanies,
        updateCompany,
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany(): CompanyContextType {
  const context = useContext(CompanyContext);
  if (!context) {
    return {
      companies: [
        {
          id: 1,
          name: 'Sai Krishna Industries',
          code: 'SKI',
          address: 'Plot No. 12, Sector 5, IMT Manesar',
          gst: '07AAAAA0000A1Z5',
          phone: '+91 98765 43210',
          email: 'info@saikrishnaindustries.com',
        },
      ],
      selectedCompanyId: 'all',
      selectedCompany: null,
      setSelectedCompanyId: () => {},
      loading: false,
      refreshCompanies: async () => {},
      updateCompany: async () => true,
    };
  }
  return context;
}
